import React from 'react';
import { Container, Typography, Button, Grid, Box, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import { styled, keyframes, useTheme } from '@mui/system';
import VoiceChatIcon from '@mui/icons-material/VoiceChat';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const AnimatedTypography = styled(Typography)(({ theme }) => ({
  animation: `${fadeIn} 1s ease-out`,
  marginBottom: theme.spacing(1),
}));

const AnimatedImage = styled('img')(({ theme }) => ({
  animation: `${float} 6s ease-in-out infinite`,
  maxWidth: '100%',
  height: 'auto',
  maxHeight: '300px',
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 3),
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(8),
}));

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const features = [
    "Advanced voice recognition",
    "Customizable AI responses",
    "24/7 Availability: Always available on demand",
    "Multi-Language Support",
    "Seamless integration with your systems",
    "Real-time analytics and insights"
  ];

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column" justifyContent="center">
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Section>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <AnimatedTypography variant={isMobile ? 'h4' : 'h3'} component="h1" color="textSecondary" mb={1}>
                Create your own Ai-Talker
              </AnimatedTypography>
              <AnimatedTypography variant={isMobile ? 'h4' : 'h3'} component="h2" color="textSecondary" mb={4}>
                Ai Call Handling Agent
              </AnimatedTypography>
              <Box mt={4}>
                <StyledButton
                  component={Link}
                  to="/onboarding/profile"
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<VoiceChatIcon />}
                >
                  GET STARTED WITH AITALKER
                </StyledButton>
                <StyledButton
                  component={Link}
                  to="/learn-more"
                  variant="outlined"
                  size="large"
                  startIcon={<InfoIcon />}
                >
                  LEARN MORE
                </StyledButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="center">
                <AnimatedImage
                  src={`${process.env.PUBLIC_URL}/images/bluebot.png`}
                  alt="AiTalker Mascot"
                />
              </Box>
            </Grid>
          </Grid>
        </Section>

        {/* Features Section */}
        <Section>
          <Typography variant="h4" component="h3" gutterBottom fontWeight="bold" textAlign="center" mb={4}>
            Key Features of AiTalker
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureItem>
                  <CheckCircleOutlineIcon sx={{ mr: 2, color: theme.palette.success.main }} />
                  <Typography variant="body1">{feature}</Typography>
                </FeatureItem>
              </Grid>
            ))}
          </Grid>
        </Section>
      </Container>
    </Box>
  );
};

export default Home;
